'use client'

import { useEffect, useState } from 'react'
import { apiFetch } from '@/lib/api-fetch'
import { Switch } from '@/components/ui/Switch'

interface TelegramSettings {
  enabled: boolean
  chatId: string
  events: { courier_assigned_waiting_for_code: boolean; courier_message_sent: boolean; cdek_handoff_message_sent: boolean; pre_notification_sent: boolean; release_published: boolean; release_deployment_condition: boolean; host_runtime_alert: boolean; host_runtime_recovered: boolean; new_sale: boolean }
}

interface IntegrationResponse {
  settings: { telegram: TelegramSettings; updatedAt?: string }
  runtime: { environment: 'production' | 'preview' | 'local'; telegramConfigured: boolean }
  recentFailures: Array<{
    eventKeyDigest: string
    // Retired members are kept: a stored failure row naming the withdrawn
    // stock-guard family must still render rather than break this panel.
    eventType: 'courier_assigned_waiting_for_code' | 'courier_message_sent' | 'cdek_handoff_message_sent' | 'pre_notification_sent' | 'release_published' | 'release_deployment_condition' | 'stock_guard_zeroed' | 'stock_guard_unresolved' | 'stock_guard_failed' | 'stock_guard_stalled' | 'host_runtime_alert' | 'host_runtime_recovered' | 'new_sale'
    provider: string
    reason: 'provider_error' | 'provider_refused' | 'provider_unavailable' | 'unexpected_provider_outcome'
    attemptedAt: string
    source: 'durable_outcome' | 'valkey_hint'
  }>
}

const failureReasonLabels: Record<IntegrationResponse['recentFailures'][number]['reason'], string> = {
  provider_error: 'ошибка провайдера',
  provider_refused: 'провайдер отклонил уведомление',
  provider_unavailable: 'провайдер недоступен',
  unexpected_provider_outcome: 'непредвиденный ответ провайдера',
}

interface PairingSession {
  code: string
  command: string
  botUsername?: string
  expiresAt: string
}

interface PairingCandidate {
  chatId: string
  chatTitle: string
  chatType: 'group' | 'supergroup' | 'channel'
}

export function NotificationIntegrationsSettings() {
  const [data, setData] = useState<IntegrationResponse | null>(null)
  const [token, setToken] = useState('')
  const [pairing, setPairing] = useState<PairingSession | null>(null)
  const [candidate, setCandidate] = useState<PairingCandidate | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    let cancelled = false
    apiFetch('/api/settings/integrations')
      .then(async response => {
        const body = await response.json()
        if (!response.ok) throw new Error(body.error || 'Не удалось загрузить интеграции')
        if (!cancelled) setData(body)
      })
      .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Ошибка загрузки') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!pairing || candidate) return
    let cancelled = false
    const timer = window.setInterval(async () => {
      try {
        const response = await apiFetch('/api/settings/integrations/telegram/pair', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'check', code: pairing.code }),
        })
        const body = await response.json()
        if (!response.ok) throw new Error(body.error || 'Не удалось проверить подключение')
        if (!cancelled && body.status === 'found') setCandidate(body.candidate)
        if (!cancelled && body.status === 'expired') {
          setPairing(null)
          setError('Код подключения истёк. Начните заново.')
        }
      } catch {
        // A transient polling failure should not discard a still-valid pairing code.
      }
    }, 2_000)
    return () => { cancelled = true; window.clearInterval(timer) }
  }, [pairing, candidate])

  function updateTelegram(patch: Partial<TelegramSettings>) {
    setData(current => current ? {
      ...current,
      settings: { ...current.settings, telegram: { ...current.settings.telegram, ...patch } },
    } : current)
  }

  async function save() {
    if (!data || busy) return
    setBusy('save'); setError(''); setNotice('')
    try {
      const response = await apiFetch('/api/settings/integrations', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telegram: data.settings.telegram, ...(token ? { token } : {}) }),
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Не удалось сохранить интеграцию')
      setData(body); setToken(''); setNotice('Настройки Telegram сохранены')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Ошибка сохранения')
    } finally { setBusy('') }
  }

  async function startPairing() {
    if (busy) return
    setBusy('pair'); setError(''); setNotice(''); setCandidate(null)
    try {
      const response = await apiFetch('/api/settings/integrations/telegram/pair', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'start' }),
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Не удалось начать подключение')
      setPairing(body)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Ошибка подключения')
    } finally { setBusy('') }
  }

  async function approvePairing() {
    if (!pairing || !candidate || busy) return
    setBusy('approve'); setError(''); setNotice('')
    try {
      const response = await apiFetch('/api/settings/integrations/telegram/pair', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'approve', code: pairing.code }),
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Не удалось подтвердить чат')
      setData(current => current ? { ...current, settings: body.settings } : current)
      setPairing(null); setCandidate(null)
      setNotice(`Чат «${body.candidate.chatTitle}» подключён. Бот отправил подтверждение.`)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Ошибка подтверждения')
    } finally { setBusy('') }
  }

  async function test(send: boolean) {
    if (!data || busy) return
    setBusy(send ? 'send-test' : 'verify'); setError(''); setNotice('')
    try {
      const response = await apiFetch('/api/settings/integrations/telegram/test', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ send }),
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.detail || body.error || 'Не удалось проверить Telegram')
      setNotice(send ? `Тестовое сообщение отправлено в «${body.connection.chatTitle}»` : `Бот видит чат «${body.connection.chatTitle}»`)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Ошибка проверки Telegram')
    } finally { setBusy('') }
  }

  const telegram = data?.settings.telegram
  const candidateType = candidate?.chatType === 'channel' ? 'канал' : 'группа'
  return <section className="mb-12">
    <div className="mb-5">
      <h2 className="text-[18px] font-semibold tracking-tight text-ink">Интеграции уведомлений</h2>
      <div className="mt-0.5 text-[13px] text-ink-muted">Подключение серверных событий к внешним каналам. Сейчас доступен Telegram.</div>
    </div>
    <div className={`surface-card rounded-md p-4 sm:p-5 ${telegram?.enabled ? 'border-gold' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-semibold text-ink">Telegram Bot</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${loading ? 'bg-bone text-ink-muted' : data?.runtime.telegramConfigured ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
              {loading ? 'Загружаем…' : data?.runtime.telegramConfigured ? 'Токен сохранён' : 'Нужен токен'}
            </span>
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-ink-muted">Токен бота хранится в Redis в зашифрованном виде и никогда не возвращается на эту страницу.</p>
        </div>
        <Switch checked={Boolean(telegram?.enabled)} label="Уведомления Telegram" disabled={!telegram || loading} onChange={() => { if (telegram) updateTelegram({ enabled: !telegram.enabled }) }} />
      </div>

      <label className="mt-5 grid gap-1.5 text-[12px] font-medium text-ink-2">
        Токен от BotFather
        <input type="password" autoComplete="off" value={token} onChange={event => setToken(event.target.value)} placeholder={data?.runtime.telegramConfigured ? 'Оставьте пустым, чтобы сохранить текущий токен' : '123456789:AA…'}
          className="rounded-sm border border-rule-strong bg-paper px-3 py-2.5 font-mono text-[13px] text-ink outline-none focus:border-gold-deep" />
      </label>

      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={save} disabled={!data || Boolean(busy)} className="min-h-10 rounded-sm bg-onyx px-4 py-2 text-[13px] font-semibold text-ivory disabled:opacity-50">
          {busy === 'save' ? 'Сохраняем…' : data?.runtime.telegramConfigured ? 'Сохранить настройки' : 'Проверить и сохранить токен'}
        </button>
        <button type="button" onClick={startPairing} disabled={!data?.runtime.telegramConfigured || Boolean(busy)} className="min-h-10 rounded-sm border border-rule-strong bg-paper px-4 py-2 text-[13px] font-semibold text-ink disabled:opacity-50">
          {busy === 'pair' ? 'Подготавливаем…' : 'Подключить группу'}
        </button>
      </div>

      {pairing && <div className="mt-5 rounded-sm border border-blue-200 bg-blue-50 p-4 text-[12px] text-blue-950">
        <div className="font-semibold">Добавьте @{pairing.botUsername || 'бота'} в группу и отправьте команду:</div>
        <button type="button" onClick={() => navigator.clipboard.writeText(pairing.command)} className="mt-2 max-w-full break-all rounded-sm bg-white px-3 py-2 font-mono text-[13px] font-semibold shadow-sm">{pairing.command}</button>
        <div className="mt-2 text-blue-700">Ждём команду. Код действует 10 минут.</div>
        {candidate && <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-blue-200 pt-4">
          <div><span className="font-semibold">Найдена группа:</span> {candidate.chatTitle} <span className="text-blue-600">({candidateType})</span></div>
          <button type="button" onClick={approvePairing} disabled={Boolean(busy)} className="rounded-sm bg-blue-950 px-4 py-2 font-semibold text-white disabled:opacity-50">{busy === 'approve' ? 'Подключаем…' : 'Подтвердить и подключить'}</button>
        </div>}
      </div>}

      {telegram?.chatId && <div className="mt-5 rounded-sm border border-rule bg-bone/40 p-3 text-[12px] text-ink-muted">Подключённый чат: <code className="font-semibold text-ink">{telegram.chatId}</code></div>}

      <div className="mt-5 grid gap-2 border-t border-rule pt-4 sm:grid-cols-2">
        <label className="flex items-center gap-2 text-[12px] text-ink-2"><input type="checkbox" checked={Boolean(telegram?.events.new_sale)} onChange={event => telegram && updateTelegram({ events: { ...telegram.events, new_sale: event.target.checked } })} />Новая продажа</label>
        <label className="flex items-center gap-2 text-[12px] text-ink-2"><input type="checkbox" checked={Boolean(telegram?.events.courier_message_sent)} onChange={event => telegram && updateTelegram({ events: { ...telegram.events, courier_message_sent: event.target.checked } })} />Сообщение о курьере отправлено</label>
        <label className="flex items-center gap-2 text-[12px] text-ink-2"><input type="checkbox" checked={Boolean(telegram?.events.cdek_handoff_message_sent)} onChange={event => telegram && updateTelegram({ events: { ...telegram.events, cdek_handoff_message_sent: event.target.checked } })} />Подтверждение передачи в СДЭК отправлено</label>
        <label className="flex items-center gap-2 text-[12px] text-ink-2"><input type="checkbox" checked={Boolean(telegram?.events.pre_notification_sent)} onChange={event => telegram && updateTelegram({ events: { ...telegram.events, pre_notification_sent: event.target.checked } })} />Предуведомление до отправки отправлено</label>
        <label className="flex items-center gap-2 text-[12px] text-ink-2"><input type="checkbox" checked={Boolean(telegram?.events.release_published)} onChange={event => telegram && updateTelegram({ events: { ...telegram.events, release_published: event.target.checked } })} />Вышла новая версия приложения</label>
        <label className="flex items-center gap-2 text-[12px] text-ink-2"><input type="checkbox" checked={Boolean(telegram?.events.release_deployment_condition)} onChange={event => telegram && updateTelegram({ events: { ...telegram.events, release_deployment_condition: event.target.checked } })} />Выпуск принят с замечанием по инфраструктуре</label>
        <label className="flex items-center gap-2 text-[12px] text-ink-2"><input type="checkbox" checked={Boolean(telegram?.events.host_runtime_alert)} onChange={event => telegram && updateTelegram({ events: { ...telegram.events, host_runtime_alert: event.target.checked } })} />Новый сервер: требуется проверка</label>
        <label className="flex items-center gap-2 text-[12px] text-ink-2"><input type="checkbox" checked={Boolean(telegram?.events.host_runtime_recovered)} onChange={event => telegram && updateTelegram({ events: { ...telegram.events, host_runtime_recovered: event.target.checked } })} />Новый сервер: работа восстановлена</label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={() => test(false)} disabled={!telegram?.chatId || Boolean(busy)} className="min-h-10 rounded-sm border border-rule-strong bg-paper px-4 py-2 text-[13px] font-semibold text-ink disabled:opacity-50">{busy === 'verify' ? 'Проверяем…' : 'Проверить без отправки'}</button>
        <button type="button" onClick={() => test(true)} disabled={!telegram?.chatId || Boolean(busy)} className="min-h-10 rounded-sm border border-rule-strong bg-paper px-4 py-2 text-[13px] font-semibold text-ink disabled:opacity-50">{busy === 'send-test' ? 'Отправляем…' : 'Отправить тест'}</button>
      </div>
      {error && <div role="alert" className="mt-4 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</div>}
      {notice && <div role="status" className="mt-4 rounded-sm border border-green-200 bg-green-50 px-3 py-2 text-[12px] text-green-800">{notice}</div>}
      {Boolean(data?.recentFailures.length) && <div className="mt-5 border-t border-rule pt-4">
        <div className="text-[12px] font-semibold text-ink">Последние ошибки доставки</div>
        <div className="mt-2 grid gap-2">
          {data?.recentFailures.map(failure => <div key={`${failure.eventKeyDigest}:${failure.attemptedAt}`} className="rounded-sm border border-red-100 bg-red-50/60 px-3 py-2 text-[11px] text-red-800">
            <span className="font-semibold">{failure.provider}</span> · {failure.eventType === 'new_sale' ? 'новая продажа' : failure.eventType === 'stock_guard_zeroed' ? 'защита остатков: остаток обнулён' : failure.eventType === 'stock_guard_unresolved' ? 'защита остатков: риск двойной продажи' : failure.eventType === 'stock_guard_failed' ? 'защита остатков: выполнение не удалось' : failure.eventType === 'stock_guard_stalled' ? 'защита остатков: изменение не подтвердилось' : failure.eventType === 'release_deployment_condition' ? 'замечание по инфраструктуре после релиза' : failure.eventType === 'release_published' ? 'релиз' : failure.eventType === 'cdek_handoff_message_sent' ? 'подтверждение передачи в СДЭК' : failure.eventType === 'pre_notification_sent' ? 'предуведомление до отправки' : failure.eventType === 'courier_assigned_waiting_for_code' ? 'курьер назначен, ждём код' : 'сообщение о курьере'} · {failureReasonLabels[failure.reason]} · {new Date(failure.attemptedAt).toLocaleString('ru-RU', { timeZone: 'Europe/Riga' })}{failure.source === 'valkey_hint' ? ' · подсказка кэша' : ''}
          </div>)}
        </div>
      </div>}
    </div>
  </section>
}
